import { toObservable } from '@equinor/fusion-observable';
import { TelemetryType, TelemetryItemNames } from '../static.js';
import { merge, of } from 'rxjs';
import { catchError, defaultIfEmpty, first, map } from 'rxjs/operators';
import { mergeTelemetryItem } from './merge-telemetry-item.js';
import { TelemetryExceptionSchema } from '../schemas.js';
/**
 * Resolves metadata from a {@link MetadataExtractor} into an observable stream.
 *
 * Converts the extractor (which may be a static value, promise, function, or observable)
 * into a normalised `Observable` that emits the resolved metadata record.
 *
 * @param metadata - The metadata extractor to resolve.
 * @param args - Context passed to the extractor, including the telemetry item and optional module instances.
 * @returns An `Observable` emitting the resolved metadata record.
 */
export const resolveMetadata = (metadata, args) => {
    return toObservable(metadata, args);
};
/**
 * Applies metadata to a telemetry item by resolving metadata asynchronously.
 *
 * This function takes a metadata extractor and its arguments, resolves the metadata,
 * and merges it into the provided telemetry item. If an error occurs during metadata resolution,
 * it emits both an error telemetry item and the original item.
 *
 * @remarks
 * - When resolving metadata, it will only take the first emitted value!.
 * - The metadata extractor can be a function or an object that implements the `DynamicInputValue` interface.
 * - The `args` parameter must include the telemetry item from which metadata is extracted.
 * - The function returns an Observable that emits the telemetry item with applied metadata or an error telemetry item if resolution fails.
 *
 * @param metadata - The metadata extractor function or object used to resolve metadata.
 * @param args - The arguments required by the metadata extractor, including the telemetry item.
 * @returns An Observable emitting the telemetry item with applied metadata, or an error telemetry item if resolution fails.
 */
// Deliberately co-located with `resolveMetadata` above
// fusion-lint-disable-next-line single-export-per-file
export const applyMetadata = (metadata, args) => {
    // Resolve metadata, merge it into the item, and fall back to an error item on failure
    return resolveMetadata(metadata, args).pipe(defaultIfEmpty({}), // Ensure we always have an object to merge with
    first(), // Take the first emitted value from the metadata extractor
    // Merge the resolved metadata with the telemetry item.
    // resolved metadata is always target, else item metadata would be overwritten
    map((data) => mergeTelemetryItem({ metadata: data }, args.item)), catchError((error) => {
        const errorItem = TelemetryExceptionSchema.parse({
            type: TelemetryType.Exception,
            name: TelemetryItemNames.MetadataError,
            exception: error,
            properties: {
                sourceMetricName: args.item.name,
                sourceMetricType: args.item.type,
            },
        });
        return merge(of(errorItem), of(args.item));
    }));
};
//# sourceMappingURL=resolve-metadata.js.map